/**
 * @fileoverview Plain-text ⇄ HTML helpers for the rich-text mail composer.
 *
 * Email drafts store both a plain-text part (`bodyText`, always sent as the
 * text/plain MIME part) and an optional rich HTML part (`bodyHtml`, sent as
 * text/html). The composer edits HTML; these helpers keep the two in sync:
 *
 * - `plainTextToHtml` seeds the editor from a plain-text draft (mirrors the
 *   server's fallback derivation in `sendFromMailbox`) so what the reviewer
 *   sees matches what would be sent.
 * - `htmlToPlainText` derives the text/plain part from the edited HTML so
 *   text-only clients still get a readable message.
 */

/** Escape the HTML-significant characters in a plain-text run. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Convert a plain-text body into simple paragraph HTML. Blank lines separate
 * paragraphs; single newlines become `<br>`. Mirrors the server-side fallback
 * so a never-edited draft round-trips identically.
 */
export function plainTextToHtml(text: string): string {
  if (text.trim().length === 0) return "";
  return text
    .split(/\n{2,}/)
    .map(
      (paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`,
    )
    .join("");
}

/**
 * Derive a readable plain-text body from composer HTML. Block-level elements
 * and `<br>` become line breaks; list items are prefixed with a bullet. Uses
 * the DOM to decode entities and strip remaining markup.
 */
export function htmlToPlainText(html: string): string {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "\u2022 ")
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n");
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  const text = doc.body.textContent ?? "";
  return text
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}
